import { useAuth0 } from "@auth0/auth0-react";
import { useProfileQuery } from "../user/profile";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../utils";
import request from "@/lib/api/handler";
import { useEffect, useRef } from "react";
import { getAuthedSocket } from "@/realtime/getSocket";

export interface Executive {
  id: string;
  name?: string;
  profilePicture?: string;
  role?: string;
  engagementType?: string;
  experience?: string;
  startDate?: string;
  linkedIn?: string;
  cultureLink?: string;
  biography?: string;
}

export interface Company {
  id?: string;
  name?: string;
  location?: string;
  logoLink?: string;
  foundedYear?: string;
  companySize?: string;
  fundsRaised?: string;
  lastFundraise?: string;
  cultureStatements?: string;
  linkedIn?: string;
  websiteLink?: string;
  aboutUs?: string;
  missionStatement?: string;
  profileComplete?: boolean;
  onboardingComplete?: boolean;
  researchInProgress?: boolean;
  executiveTeam: Executive[];
}

export const useMyCompanyQuery = (companyId?: string) => {
  const qc = useQueryClient();
  const { profile } = useProfileQuery();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const joinedRef = useRef(false);
  const handlerRef = useRef<((incoming: Company) => void) | null>(null);

  const cid = companyId ?? profile?.companyAccounts[0]?.companyId;

  const { isLoading, data, error } = useQuery({
    queryKey: QueryKeys.myCompany(cid ?? ""),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!cid,
    async queryFn() {
      const token = await getAccessTokenSilently();
      const resp: Company = await request({
        method: "GET",
        path: `/api/company/${cid}`,
        token,
      });

      return resp;
    },
  });

  useEffect(() => {
    if (!cid || !isAuthenticated) {
      return;
    }

    let cancelled = false;

    void (async () => {
      if (cancelled) {
        return;
      }

      const socket = await getAuthedSocket(getAccessTokenSilently);

      if (!data?.researchInProgress) {
        if (joinedRef.current) {
          socket.emit("company_profile:unsubscribe", cid);
          joinedRef.current = false;
        }

        if (handlerRef.current) {
          socket.off("company_profile:update", handlerRef.current);
          handlerRef.current = null;
        }

        return;
      }

      if (!joinedRef.current) {
        socket.emit("company_profile:subscribe", cid);
        joinedRef.current = true;
      }

      // Stable handler
      const onUpdate = (incoming: Company) => {
        if (incoming.id !== cid) {
          return;
        }

        // Update cache from payload (no refetch)
        qc.setQueryData<Company>(QueryKeys.myCompany(cid), incoming);

        // If completed, leave and detach
        if (!incoming.researchInProgress) {
          socket.emit("company_profile:unsubscribe", cid);
          joinedRef.current = false;

          if (handlerRef.current) {
            socket.off("company_profile:update", handlerRef.current);
            handlerRef.current = null;
          }
        }
      };

      // Replace old handler
      if (handlerRef.current) {
        socket.off("company_profile:update", handlerRef.current);
      }

      handlerRef.current = onUpdate;
      socket.on("company_profile:update", onUpdate);
    })();

    return () => {
      cancelled = true;

      void (async () => {
        try {
          const socket = await getAuthedSocket(getAccessTokenSilently);
          if (handlerRef.current) {
            socket.off("company_profile:update", handlerRef.current);
            handlerRef.current = null;
          }
          if (joinedRef.current) {
            socket.emit("company_profile:unsubscribe", cid);
            joinedRef.current = false;
          }
        } catch {
          /* ignore */
        }
      })();
    };
  }, [isAuthenticated, cid, data, qc, getAccessTokenSilently]);

  return {
    companyLoading: isLoading,
    company: data ?? null,
    companyError: error ?? null,
  };
};
